import Grid from '@mui/material/Grid2'
import BorderedSection from 'Components/reusable/CaleoCustomComponents/BorderedSection'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ISearchResult, ISearchFields } from 'types/searchInterfaces'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { getCV } from 'utils/getCV'
import AssignmentProposalModal from 'Components/General/AssignmentProposalModal/AssignmentProposalModal'
import { assignmentProposalSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { getAssignmentProposalInitialData } from 'Components/reusable/DataContext/InitialData'
import { CountriesData } from 'api/UtilsAPI'
import ResultContent from './ResultContent'
import FreeTextMatchInfo from './FreeTextMatchInfo'
import colors from 'constants/colors'
import { useUser } from 'hooks'

/** @notExported */
interface IResultCardProps {
  /** The search result. */
  result: ISearchResult
  /** The search terms. */
  terms: ISearchFields
  /** The list of translated country options. */
  countries: CountriesData
}

/**
 * Renders the result card component with specific content based on the result kind.
 *
 * @param {IResultCardProps} result - the result object to be displayed
 * @param {ISearchFields} terms - the search terms used to obtain the result
 * @param {CountriesData} countries - the CountriesData used for rendering the result
 * @return {React.ReactElement} the result card component
 */
const ResultCard: React.FC<IResultCardProps> = ({ result, terms, countries }) => {
  const { t } = useTranslation()
  const { features } = useUser()
  const [openProposal, setOpenProposal] = useState<string>()
  // Without it, ResultContent shows no propose button.
  const proposeHandler = features.includes('searchProposals') ? setOpenProposal : undefined

  const cv = result.Person.CVs ? result.Person.CVs[0] : getCV(result.Person, terms)

  const hasSimilarSkills = result.similarSkills && result.similarSkills.length > 0
  const hasOnlySimilarSkills = hasSimilarSkills && (!result.matchedSkills || result.matchedSkills.length === 0)

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        {result.kind === 'exact' && (
          <BorderedSection
            color={hasOnlySimilarSkills ? colors.secondary : '#0D6AFF'}
            icon={AutoAwesomeIcon}
            title={hasOnlySimilarSkills ? t('search.similarSkillsMatch') : t('search.exactMatch')}
          >
            <ResultContent result={result} cv={cv} countries={countries} setOpenProposal={proposeHandler} />
            <FreeTextMatchInfo result={result} />
          </BorderedSection>
        )}
        {result.kind === 'partial' && (
          <Grid
            sx={{
              border: `1px solid ${colors.borderColor}`,
              borderRadius: '5px',
              padding: '1em',
              marginTop: '1em',
              marginBottom: '1em',
            }}
            size={12}
          >
            <ResultContent result={result} cv={cv} countries={countries} setOpenProposal={proposeHandler} />
            <FreeTextMatchInfo result={result} />
          </Grid>
        )}
        {result.kind === 'freeText' && (
          <BorderedSection color={colors.secondary} icon={AutoAwesomeIcon} title={t('search.freeTextMatch')}>
            <ResultContent result={result} cv={cv} countries={countries} setOpenProposal={proposeHandler} />
            <FreeTextMatchInfo result={result} />
          </BorderedSection>
        )}
      </Grid>
      {openProposal && (
        <AssignmentProposalModal
          anonymousId={openProposal}
          onClose={() => {
            setOpenProposal(undefined)
          }}
          initialData={getAssignmentProposalInitialData()}
          schema={assignmentProposalSchema()}
          localeBase="search.propose"
          submitOnModal={true}
          hideLanguageHelperText={true}
          maxWidth="md"
          fullWidth={true}
          customButtons={true}
          noEscClose
        />
      )}
    </Grid>
  )
}

export default ResultCard
